import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import GrainIcon from '@mui/icons-material/Grain';
import HayIcon from '@mui/icons-material/Grass';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Pagination from '@mui/material/Pagination';
import { UserService } from '@pingum/api-services';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { rrulestr } from 'rrule';

import {
  Medication,
  Restaurant,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import SupplementIcon from '../../../../../components/CustomIcons/Pill';
import { getHorseFeeds } from '../../../../../redux/horse/actions';
import {
  blueGrey,
  primary,
} from '../../../../../utils/brandColors';
import { getContrast } from '../../../../../utils/scheduleHelper';
import {
  isUserAdmin,
  isUserOwner,
} from '../../../../../utils/tenantAuthenticationHelper';
import AddEditModal from './components/AddEditModal';
import FeedsCardLoading from './components/FeedsCardLoading';

// eslint-disable-next-line no-unused-vars
export const getIconForFeed = (feed, contrastColor = true) => {
  const color = `${contrastColor
    ? getContrast(feed.color || primary[500])
    : (feed.color || primary[500])} !important`
  if (feed.type === 'Grain') {
    return (
      <GrainIcon
        sx={{
          color,
          height: '1.25rem !important',
          width: '1.25rem !important',
        }}
      />
    );
  }
  if (feed.type === 'Supplement') return <SupplementIcon color={color} fontSize="1.25rem" />;
  if (feed.type === 'Medicine') {
    return (
      <Medication
        sx={{
          color,
          height: '1.25rem !important',
          width: '1.25rem !important',
        }}
        size="large"
      />
    );
  }
  return (
    <HayIcon
      sx={{
        color,
        height: '1.25rem !important',
        width: '1.25rem !important',
      }}
      size="large"
    />
  );
};

const getNumberValue = (value) => {
  if (!Number.isNaN(Number(value))) {
    return Number(value).toLocaleString();
  }
  return value;
};

const getRruleString = (rrule) => {
  if (!rrule) return 'no frequency specified';
  return rrulestr(rrule).toText();
};

const getQuantity = (horseFeed) => {
  if (!horseFeed.quantity) return 'no quantity specificed';
  return `${horseFeed.quantity} ${horseFeed.quantityMeasure}`;
};

export const getFeedDescription = (horseFeed, filterTimes, chip = true) => {
  if (horseFeed.frequency.rrule.indexOf('FREQ=DAILY') >= 0) {
    const hasAM = horseFeed.frequency.am && horseFeed.frequency.am.length > 0 && horseFeed.frequency.am !== '0';
    const hasMidday = horseFeed.frequency.midday && horseFeed.frequency.midday.length > 0 && horseFeed.frequency.midday !== '0';
    const hasPM = horseFeed.frequency.pm && horseFeed.frequency.pm.length > 0 && horseFeed.frequency.pm !== '0';
    const hasEvening = horseFeed.frequency.evening && horseFeed.frequency.evening.length > 0 && horseFeed.frequency.evening !== '0';
    let RenderChip = Chip;
    if (!chip) {
      RenderChip = ({ label }) => <span>{label}</span>;
    }
    return (
      <Box>
        {!!horseFeed.notes && <div><small>&#8220;{horseFeed.notes}&#8221;</small></div>}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: 10,
            gap: 0.5,
          }}
        >
          {(!filterTimes || filterTimes.am) && (
            <Box
              sx={{
                flex: 1,
                fontSize: hasAM
                  ? 14
                  : 'inherit',
                fontWeight: hasAM
                  ? 'semi-bold'
                  : 'inherit',
              }}
            >AM
            </Box>
          )}
          {(!filterTimes || filterTimes.midday) && (
            <Box
              sx={{
                flex: 1,
                fontSize: hasMidday
                  ? 14
                  : 'inherit',
                fontWeight: hasMidday
                  ? 'semi-bold'
                  : 'inherit',
              }}
            >Midday
            </Box>
          )}
          {(!filterTimes || filterTimes.pm) && (
            <Box
              sx={{
                flex: 1,
                fontSize: hasPM
                  ? 14
                  : 'inherit',
                fontWeight: hasPM
                  ? 'semi-bold'
                  : 'inherit',
              }}
            >PM
            </Box>
          )}
          {(!filterTimes || filterTimes.evening) && (
            <Box
              sx={{
                flex: 1,
                fontSize: hasEvening
                  ? 14
                  : 'inherit',
                fontWeight: hasEvening
                  ? 'semi-bold'
                  : 'inherit',
              }}
            >Evening
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {(!filterTimes || filterTimes.am) && <Box sx={{ flex: 1 }}>{(hasAM && (<RenderChip size="small" label={`${getNumberValue(horseFeed.frequency.am)} ${horseFeed.quantityMeasure}`} />)) || ''}</Box>}
          {(!filterTimes || filterTimes.midday) && <Box sx={{ flex: 1 }}>{(hasMidday && (<RenderChip size="small" label={`${getNumberValue(horseFeed.frequency.midday)} ${horseFeed.quantityMeasure}`} />)) || ''}</Box>}
          {(!filterTimes || filterTimes.pm) && <Box sx={{ flex: 1 }}>{(hasPM && (<RenderChip size="small" label={`${getNumberValue(horseFeed.frequency.pm)} ${horseFeed.quantityMeasure}`} />)) || ''}</Box>}
          {(!filterTimes || filterTimes.evening) && <Box sx={{ flex: 1 }}>{(hasEvening && (<RenderChip size="small" label={`${getNumberValue(horseFeed.frequency.evening)} ${horseFeed.quantityMeasure}`} />)) || ''}</Box>}
        </Box>
      </Box>
    )
  }

  return (
    <>
      {`${getQuantity(horseFeed)} - ${getRruleString(horseFeed.frequency.rrule)}`}
      {!!horseFeed.notes && <div><small>&#8220;{horseFeed.notes}&#8221;</small></div>}
    </>
  )
}

export const getFeedDescriptionTableColumn = (horseFeed, filterTimes) => {
  if (horseFeed.frequency.rrule.indexOf('FREQ=DAILY') >= 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
            borderRight: `1px solid ${'var(--blueGrey-unknown)'}`,
          }}
          style={{ '--blueGrey-unknown': blueGrey[100] }}
        >{((!filterTimes || filterTimes.am) && horseFeed.frequency.am && horseFeed.frequency.am.length > 0 && horseFeed.frequency.am !== '0')
          ? <>{getNumberValue(horseFeed.frequency.am)}<br />{horseFeed.quantityMeasure}</>
          : '—'}
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
            borderRight: `1px solid ${'var(--blueGrey-unknown)'}`,
          }}
          style={{ '--blueGrey-unknown': blueGrey[100] }}
        >{((!filterTimes || filterTimes.midday) && horseFeed.frequency.midday && horseFeed.frequency.midday.length > 0 && horseFeed.frequency.midday !== '0')
          ? <>{getNumberValue(horseFeed.frequency.midday)}<br />{horseFeed.quantityMeasure}</>
          : '—'}
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
            borderRight: `1px solid ${'var(--blueGrey-unknown)'}`,
          }}
          style={{ '--blueGrey-unknown': blueGrey[100] }}
        >{((!filterTimes || filterTimes.pm) && horseFeed.frequency.pm && horseFeed.frequency.pm.length > 0 && horseFeed.frequency.pm !== '0')
          ? <>{getNumberValue(horseFeed.frequency.pm)}<br />{horseFeed.quantityMeasure}</>
          : '—'}
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
          }}
        >
          {((!filterTimes || filterTimes.evening) && horseFeed.frequency.evening && horseFeed.frequency.evening.length > 0 && horseFeed.frequency.evening !== '0')
            ? <>{getNumberValue(horseFeed.frequency.evening)}<br />{horseFeed.quantityMeasure}</>
            : '—'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {`${getQuantity(horseFeed)} - ${getRruleString(horseFeed.frequency.rrule)}`}
    </Box>
  )
}

const FeedsCardComponent = ({ id }) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    horseFeeds,
    isFetchingHorseFeeds,
  } = useSelector((state) => state.horse);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const { user } = useSelector((state) => state.user);

  // State
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorseFeed, setSelectedHorseFeed] = React.useState(null);
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'feed.order',
      sort: 'asc',
    },
    {
      field: 'feed.name',
      sort: 'asc',
    },
  ]);

  const roles = UserService.getUserRolesForTenant(
    user,
    selectedBarn.tenant,
  );
  // eslint-disable-next-line no-unused-vars
  const isAdmin = isUserAdmin(roles) || isUserOwner(roles);

  React.useEffect(() => {
    dispatch(getHorseFeeds(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id, page]);

  const handleAddClick = () => {
    setSelectedHorseFeed(null);
    setOpenAddEditModal(true);
  };

  const handleEditClick = (horseFeed) => {
    setSelectedHorseFeed(horseFeed);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    // reload
    dispatch(getHorseFeeds(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  };

  return (
    <>
      {isFetchingHorseFeeds
        ? <FeedsCardLoading />
        : (
          <Paper
            sx={{
              borderRadius: 3,
              p: 2,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={2}
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar><Restaurant /></Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Feed
                </Typography>
              </Stack>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddClick}>
                Add Feed
              </Button>
            </Stack>
            <Box>
              <List
                aria-label="horse feed"
              >
                {
                  horseFeeds.content.length === 0
                    ? (
                      <ListItemButton disabled><ListItemText secondary="No horse feed specified" /></ListItemButton>
                    )
                    : horseFeeds.content.map((horseFeed) => (
                      <ListItem
                        key={horseFeed.id}
                        secondaryAction={(
                          <IconButton edge="end" aria-label="edit" onClick={() => { handleEditClick(horseFeed); }}>
                            <EditIcon />
                          </IconButton>
                        )}
                        disablePadding
                      >
                        <ListItemButton>
                          <ListItemIcon>
                            <Badge
                              badgeContent={horseFeed.feed.order}
                              color="secondary"
                              sx={{ zIndex: 100 }}
                            >
                              {getIconForFeed(horseFeed.feed, false)}
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            sx={{ ml: 2 }}
                            primary={horseFeed.feed.name}
                            secondary={getFeedDescription(horseFeed)}
                            secondaryTypographyProps={{
                              component: 'div',
                              sx: { textWrap: 'auto' },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                }
              </List>
              {(horseFeeds.totalPages || 0) > 1 && (
                <Pagination count={horseFeeds.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorseFeeds} />
              )}
            </Box>
          </Paper>
        )}
      <AddEditModal
        open={openAddEditModal}
        horseFeed={selectedHorseFeed}
        horseId={id}
        onClose={handleCloseAddEditModal}
      />
    </>
  );
};

FeedsCardComponent.propTypes = { id: PropTypes.string.isRequired };

FeedsCardComponent.defaultProps = {};

export default FeedsCardComponent;
